import { OutlinedButton, PrimaryButton } from "@/Components/v2/atoms/Button";
import { Dialog, DialogContent } from "@/Components/v2/atoms/Dialog";
import { Grid } from "@/Components/v2/atoms/Grid";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { HIDE_DECIMAL, useConfig } from "@/hooks/useConfig";
import { formatDateLocale } from "@/shared/utils/formatDate";
import dayjs from "dayjs";
import { ScenarioOption } from "./NewActionPlanModal/types";
import { formatPercentageFromFraction } from "@/pages/scenario-builder/utils/formatPercentage";
import { Box } from "@/Components/v2/atoms/Box";
import { useTheme } from "@mui/material";
import React from "react";

type ApproveScenarioForActionPlanModalProps = {
  open: boolean;
  onClose: () => void;
  onApprove: () => void;
  isApprovePending: boolean;
  scenarioOption: ScenarioOption;
  actionPlan: {
    name: string;
    uuid: string;
  } | null;
};

const ApproveScenarioForActionPlanModal = ({
  open,
  onClose,
  onApprove,
  isApprovePending,
  scenarioOption,
  actionPlan,
}: ApproveScenarioForActionPlanModalProps) => {
  const { config } = useConfig();
  const formatCurrency = config.currency.formatCurrency;
  const theme = useTheme();

  const scenarioDetails = [
    {
      label: "Business Unit",
      value: scenarioOption.businessUnit,
    },
    {
      label: "Timeframe",
      value: `${formatDateLocale(dayjs(scenarioOption.startDate), { dateFormat: "DD MMM YYYY" })} - ${formatDateLocale(dayjs(scenarioOption.endDate), { dateFormat: "DD MMM YYYY" })}`,
    },
    {
      label: "Planned Spend",
      value: formatCurrency(scenarioOption.plannedSpend, HIDE_DECIMAL),
    },
    {
      label: "Forecast Revenue",
      value: formatCurrency(scenarioOption.forecastRevenue, HIDE_DECIMAL),
    },
    {
      label: "Estimated ROI",
      value: scenarioOption.estimatedROI
        ? formatPercentageFromFraction(scenarioOption.estimatedROI)
        : "N/A",
    },
  ];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: "40rem",
        },
      }}
    >
      <DialogContent
        sx={{
          position: "relative",
          p: 4,
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="h5" fontWeight={600} fontSize={20}>
            Approve this scenario?
          </Typography>
          <Typography variant="body2" color="text.secondary" fontSize={14}>
            <Typography fontWeight={600} component="span" fontSize={14}>
              Action Plan:
            </Typography>{" "}
            {actionPlan?.name}
          </Typography>
          <Typography fontSize={14}>
            Your scenario &quot;{scenarioOption.name}&quot; is currently assigned to the Action Plan
            &quot;{actionPlan?.name}&quot;.
          </Typography>
          <Typography fontSize={14}>
            If you approve this scenario, it will become the new tracked scenario for that Action
            Plan. This will change your spend and revenue forecasts. Are you sure you want to
            continue?
          </Typography>
          <Box
            sx={{
              backgroundColor: theme.palette.mode === "light" ? "#F0F3FF" : "#2E2E2E",
              p: 3,
              borderRadius: 2,
            }}
          >
            <Grid container spacing={2}>
              {scenarioDetails.map((detail) => (
                <React.Fragment key={detail.label}>
                  <Grid item xs={5}>
                    <Typography fontSize={14} fontWeight={600}>
                      {detail.label}
                    </Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography fontSize={14}>{detail.value}</Typography>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </Box>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <OutlinedButton onClick={onClose}>Go Back</OutlinedButton>
            <PrimaryButton
              sx={{ backgroundColor: theme.palette.success.main }}
              onClick={onApprove}
              disabled={isApprovePending}
            >
              {isApprovePending ? "Approving..." : "Approve Scenario"}
            </PrimaryButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ApproveScenarioForActionPlanModal;
